import React, { useEffect, useState } from "react";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { getContactDisplayName } from "../../helpers/contactDisplayName";

const createRequestKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `gns-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const intakePriority = (impact) => ({
  Bajo: "baja",
  Normal: "normal",
  Alto: "alta",
  Critico: "urgente",
}[impact] || "normal");

const intakeDescription = (intake) => [
  `Categoria: ${intake.category || "No indicada"}`,
  `Relacion declarada: ${intake.customerType || "Por validar"}`,
  `Empresa: ${intake.companyName || "No indicada"}`,
  `Impacto: ${intake.impact || "Normal"}`,
  `Horario preferido: ${intake.preferredSchedule || "No indicado"}`,
  "",
  intake.description || ""
].join("\n");

const clientRelationshipLabel = (client) => {
  if (client?.customerType === "contract") return "Contrato registrado";
  if (client?.customerType === "occasional") return "Cliente ocasional";
  return "Por validar";
};

const intakeVerificationLabel = (intake) => {
  if (intake?.verificationStatus === "verified") {
    return `Verificado: ${intake.verifiedGnsOsClientName || "Cliente GNS-OS"} (${intake.verifiedCustomerType === "contract" ? "Contrato registrado" : "Cliente ocasional"})`;
  }
  return "No verificado: validar antes de derivar a una cola contractual.";
};

const GnsOsSupportTicketModal = ({ modalOpen, onClose, ticket }) => {
	const displayName = getContactDisplayName(ticket.contact);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notifyContact, setNotifyContact] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [links, setLinks] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(null);
  const [requestKey, setRequestKey] = useState(createRequestKey);

  useEffect(() => {
    if (!modalOpen || !ticket.id) return;

    const load = async () => {
      setLoadingClients(true);
      try {
        const [clientsResponse, linkResponse] = await Promise.all([
          api.get("/tickets/gns-os/clients"),
          api.get(`/tickets/${ticket.id}/gns-os-support-ticket`),
        ]);
        setClients(clientsResponse.data.clients || []);
        setLinks(linkResponse.data.links || []);
        const loadedIntakes = linkResponse.data.intakes || [];
        const submittedIntake = loadedIntakes.find((intake) => intake.status === "submitted");
        setIntakes(loadedIntakes);
        setSelectedIntake(submittedIntake || null);
        if (submittedIntake) {
          setTitle(`Soporte WhatsApp - ${submittedIntake.category || "Solicitud"}`);
          setDescription(intakeDescription(submittedIntake));
          setPriority(intakePriority(submittedIntake.impact));
        } else {
			setTitle((current) => current || `Soporte WhatsApp - ${displayName || ticket.id}`);
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingClients(false);
      }
    };

    load();
  }, [modalOpen, ticket.id, ticket.contact]);

  const handleClose = () => {
    setSelectedClient(null);
    setDescription("");
    setPriority("normal");
    setNotifyContact(true);
    setLinks([]);
    setIntakes([]);
    setSelectedIntake(null);
    setRequestKey(createRequestKey());
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedClient) return;

    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/gns-os-support-ticket`, {
        gnsOsClientId: selectedClient.id,
        title,
        description,
        priority,
        notifyContact,
        requestKey,
        supportIntakeId: selectedIntake?.id,
      });
      setLinks((current) => [data.link, ...current]);
      if (selectedIntake) {
        setIntakes((current) => current.map((intake) =>
          intake.id === selectedIntake.id
            ? { ...intake, status: "linked", gnsOsTicketId: data.link.gnsOsTicketId }
            : intake
        ));
      }
      setSelectedClient(null);
      setSelectedIntake(null);
      setTitle("");
      setDescription("");
      setRequestKey(createRequestKey());
      toast.success(`Ticket GNS-OS #${data.link.gnsOsTicketId} creado`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Crear ticket de soporte en GNS-OS</DialogTitle>
        <DialogContent dividers>
          {links.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <strong>Solicitudes creadas en esta conversación</strong>
              {links.map((item) => (
                <TextField
                  key={item.id}
                  fullWidth
                  variant="outlined"
                  label="Ticket GNS-OS"
                  value={`#${item.gnsOsTicketId}`}
                  InputProps={{ readOnly: true }}
                  style={{ marginTop: 10 }}
                />
              ))}
            </div>
          )}
          {selectedIntake && (
            <>
              <TextField
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                label="Solicitud enviada por el cliente"
                value={intakeDescription(selectedIntake)}
                InputProps={{ readOnly: true }}
                style={{ marginBottom: 12 }}
              />
              <TextField
                fullWidth
                variant="outlined"
                label="Verificacion de contacto"
                value={intakeVerificationLabel(selectedIntake)}
                InputProps={{ readOnly: true }}
                style={{ marginBottom: 20 }}
              />
            </>
          )}
          <>
              <Autocomplete
                options={clients}
                value={selectedClient}
                onChange={(_event, value) => setSelectedClient(value)}
                getOptionLabel={(option) => option.nombre || ""}
                renderOption={(option) => `${option.nombre} - ${clientRelationshipLabel(option)}`}
                loading={loadingClients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente de GNS-OS"
                    variant="outlined"
                    required
                    autoFocus
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingClients ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {selectedClient && (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Relacion comercial en GNS-OS"
                    value={`${clientRelationshipLabel(selectedClient)}${selectedClient.horasContratadas ? ` - ${selectedClient.horasContratadas} hs` : ""}`}
                    InputProps={{ readOnly: true }}
                    style={{ marginTop: 12 }}
                  />
                  {selectedIntake?.verificationStatus === "verified" && Number(selectedIntake.verifiedGnsOsClientId) !== Number(selectedClient.id) && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Advertencia de vinculacion"
                      value="El cliente seleccionado no coincide con el numero verificado. Confirma la relacion antes de crear el ticket."
                      InputProps={{ readOnly: true }}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </>
              )}
              <TextField
                fullWidth
                required
                variant="outlined"
                label="Asunto"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                inputProps={{ maxLength: 180 }}
                style={{ marginTop: 20 }}
              />
              <TextField
                fullWidth
                multiline
                rows={5}
                variant="outlined"
                label="Resumen para soporte"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                inputProps={{ maxLength: 6000 }}
                style={{ marginTop: 20 }}
              />
              <FormControl fullWidth variant="outlined" style={{ marginTop: 20 }}>
                <InputLabel>Prioridad</InputLabel>
                <Select value={priority} onChange={(event) => setPriority(event.target.value)} label="Prioridad">
                  <MenuItem value="baja">Baja</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={notifyContact}
                    onChange={(event) => setNotifyContact(event.target.checked)}
                  />
                )}
                label="Enviar al cliente el menú para consultar el estado"
                style={{ marginTop: 12 }}
              />
          </>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined" disabled={loading}>
            Cancelar
          </Button>
          <ButtonWithSpinner variant="contained" type="submit" color="primary" loading={loading || loadingClients}>
            {links.length > 0 ? "Crear otra solicitud" : "Crear ticket"}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default GnsOsSupportTicketModal;
